import { useEffect, useState } from 'react';
import axios from 'axios';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { featureRequestApi, phaseApi } from '../../api/endpoints';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { useProjects } from '../../context/ProjectContext';
import type { Feature, FeatureRequest, Phase } from '../../types';

interface ConvertDialogProps {
  open: boolean;
  request: FeatureRequest;
  onClose: () => void;
  onConverted: (feature: Feature) => void;
}

/** Dialog chuyển yêu cầu thành một tính năng thật — dự án + giai đoạn BẮT BUỘC. */
export function ConvertDialog({ open, request, onClose, onConverted }: ConvertDialogProps): JSX.Element {
  const { projects } = useProjects();
  const [projectId, setProjectId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [phases, setPhases] = useState<Phase[]>([]);
  const [name, setName] = useState('');
  const [copyFiles, setCopyFiles] = useState(true);
  const [converting, setConverting] = useState(false);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProjectId(request.project_id ?? '');
    setPhaseId(request.phase_id ?? '');
    setName(request.title);
    setCopyFiles(true);
    setConflict(false);
  }, [open, request]);

  useEffect(() => {
    if (!projectId) {
      setPhases([]);
      return;
    }
    let active = true;
    phaseApi
      .listByProject(projectId, { limit: 200 })
      .then((result) => {
        if (active) setPhases(result.items);
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  const handleProjectChange = (value: string) => {
    setProjectId(value);
    if (value !== request.project_id) setPhaseId('');
  };

  const handleConvert = async () => {
    if (!projectId || !phaseId) {
      notify('Vui lòng chọn dự án và giai đoạn', 'warning');
      return;
    }
    setConverting(true);
    try {
      const result = await featureRequestApi.convert(request.id, {
        project_id: projectId,
        phase_id: phaseId,
        name: name.trim() || request.title,
        copy_files: copyFiles,
      });
      if (result.skipped_file_count > 0) {
        notify(`Đã chuyển thành chức năng — ${result.skipped_file_count} file không sao chép được`, 'warning');
      } else {
        notify('Đã chuyển yêu cầu thành chức năng', 'success');
      }
      onConverted(result.feature);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setConflict(true);
      } else {
        notify(getApiErrorMessage(err, 'Không thể chuyển đổi'), 'error');
      }
    } finally {
      setConverting(false);
    }
  };

  return (
    <Dialog open={open} onClose={converting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Chuyển thành chức năng</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {conflict && (
            <Alert severity="warning">Yêu cầu này đã được xử lý — vui lòng đóng và tải lại danh sách.</Alert>
          )}
          <TextField
            label="Tên chức năng"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            disabled={conflict}
          />
          <FormControl fullWidth size="small" required disabled={conflict}>
            <InputLabel id="convert-project-label">Dự án</InputLabel>
            <Select
              labelId="convert-project-label"
              label="Dự án"
              value={projectId}
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" required disabled={!projectId || conflict}>
            <InputLabel id="convert-phase-label">Giai đoạn</InputLabel>
            <Select
              labelId="convert-phase-label"
              label="Giai đoạn"
              value={phaseId}
              onChange={(e) => setPhaseId(e.target.value)}
            >
              {phases.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox checked={copyFiles} onChange={(e) => setCopyFiles(e.target.checked)} disabled={conflict} />
            }
            label="Sao chép file ảnh sang chức năng"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={converting}>
          Huỷ
        </Button>
        <Button variant="contained" onClick={() => void handleConvert()} disabled={converting || conflict}>
          {converting ? 'Đang chuyển…' : 'Chuyển thành chức năng'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
